import { ManagedCodexDriver } from "../codex/managed-driver.js";
import { existsSync, mkdirSync, readFileSync, writeFileSync } from "node:fs";
import { dirname, join } from "node:path";
import type {
  AgentMessageItem,
  Codex as CodexClient,
  CommandExecutionItem,
  FileChangeItem,
  McpToolCallItem,
  Thread,
  ThreadEvent,
  ThreadItem,
  WebSearchItem,
} from "@openai/codex-sdk";
import type { DriverTarget } from "@skaile/workspaces/plugin-registry";
import type { Logger, TokenUsage } from "@skaile/workspaces/types";
import * as z from "zod";
import { classifyGenericErrorMessage } from "../error-classifier.js";
import { getBridgeLogger } from "../logger.js";
import { fetchProviderModels, type ModelEntry } from "../models.js";
import { BUILTIN_DRIVER_CATALOG } from "../registry.js";
import {
  type AgentConfig,
  AgentDriver,
  type AgentError,
  type AgentEvent,
  type AgentMessage,
  type AgentPromptInput,
  type DriverInfo,
} from "../types.js";

type CodexSdkModule = typeof import("@openai/codex-sdk");

/**
 * Build the explicit env whitelist forwarded to the Codex SDK child.
 *
 * Omits anything provisioned by `provision_secrets` so user credentials
 * stay out of the subprocess environment.
 *
 * @docLink packages/bridge/concepts#codex-env-whitelist
 */
function buildCodexChildEnv(parent: NodeJS.ProcessEnv): Record<string, string> {
  const out: Record<string, string> = {};
  const explicit = [
    "OPENAI_API_KEY",
    "ANTHROPIC_API_KEY",
    "PATH",
    "HOME",
    "USER",
    "HOSTNAME",
    "SKAILE_SESSION_ID",
  ];
  for (const key of explicit) {
    const v = parent[key];
    if (v !== undefined) out[key] = v;
  }
  for (const [key, value] of Object.entries(parent)) {
    if (key.startsWith("PI_") && typeof value === "string") out[key] = value;
  }
  return out;
}

/**
 * Agent driver for the OpenAI Codex SDK.
 *
 * Creates or resumes a Codex thread via `@openai/codex-sdk` (lazy-imported in `start()`)
 * and maps Codex SDK thread events to the unified {@link AgentEvent} stream.
 * OpenAI models only — uses `OPENAI_API_KEY` from `apiKeys`, `env`, or the environment.
 *
 * @remarks
 * **Event mapping** — Codex SDK items are translated: `agent_message` → `message_start/update/end`,
 * `command_execution` → Bash-shaped tool events, `mcp_tool_call` → MCP tool events,
 * `file_change` → `ApplyPatch`-shaped events, `web_search` → `WebSearch` events.
 *
 * **Sandbox** — The Codex sandbox mode, approval policy, and reasoning effort are
 * controlled via `AgentConfig.driverOptions.codex`. See {@link CodexDriverOptions}.
 *
 * **Instructions file** — System prompt and pre-rendered agent identity are combined
 * into a single instructions file written to `<cwd>/.codex/skaile-session-instructions.md`
 * and passed to the Codex SDK as `model_instructions_file`.
 *
 * @docLink packages/bridge/drivers#codex-driver
 */
export class CodexDriver extends AgentDriver {
  readonly driverInfo: DriverInfo = {
    id: "codex",
    name: "OpenAI Codex",
    modelAgnostic: false,
    supportsInBandAbort: true,
  };

  private readonly log: Logger = getBridgeLogger("codex");
  private config: AgentConfig;
  private readonly managed?: ManagedCodexDriver;
  private thread: Thread | null = null;
  private running = false;
  private abortController: AbortController | null = null;
  private pendingToolResults: AgentMessage[] = [];
  private activeAssistantItemId: string | null = null;
  private activeAssistantText = "";
  private _runtimeSessionId: string | undefined;
  /** Token usage from the most recent completed turn. */
  private _lastTokens: TokenUsage | null = null;

  constructor(config: AgentConfig) {
    super();
    this.config = config;
    const codex = config.driverOptions?.codex;
    if (
      codex?.managedRuntime ||
      (codex?.authSource !== undefined && codex.authSource !== "local")
    ) {
      this.managed = new ManagedCodexDriver(config);
      this.managed.on("agent-event", (event: AgentEvent) => this.emit("agent-event", event));
    }
  }

  override get runtimeSessionId(): string | undefined {
    return this.managed ? this.managed.runtimeSessionId : this._runtimeSessionId;
  }

  override get acceptsReplyAsPrompt(): boolean {
    return !this.managed;
  }
  override async dispose(): Promise<void> {
    if (this.managed) return this.managed.dispose();
    this.kill();
  }
  override hasPendingQuestion(): boolean {
    return this.managed?.hasPendingQuestion() ?? false;
  }
  override answerQuestion(answer: string, question?: string, requestId?: string): boolean {
    return this.managed?.answerQuestion(answer, question, requestId) ?? false;
  }
  override promptInput(input: AgentPromptInput): Promise<void> {
    return this.managed ? this.managed.promptInput(input) : super.promptInput(input);
  }

  public async start(): Promise<void> {
    if (this.managed) return this.managed.start();
    if (this.thread) return;

    let sdk: CodexSdkModule;
    try {
      sdk = await import("@openai/codex-sdk");
    } catch (err) {
      this.log.error("codex SDK load failed", err);
      throw new Error("Codex SDK not installed. Run: bun add @openai/codex-sdk");
    }

    // Explicit env whitelist for the Codex SDK child. Avoids leaking the
    // runner's full environment (which may carry user secrets provisioned by
    // the platform) into the spawned process. `this.config.env`, when
    // supplied, fully overrides the whitelist.
    const env: Record<string, string | undefined> = this.config.env
      ? { ...this.config.env }
      : buildCodexChildEnv(process.env);

    const instructionsFile = this.writeInstructionsFile();

    const client = new sdk.Codex({
      apiKey:
        this.config.apiKeys?.openai ??
        this.config.env?.OPENAI_API_KEY ??
        process.env.OPENAI_API_KEY,
      env: Object.fromEntries(Object.entries(env).filter(([, value]) => value != null)) as Record<
        string,
        string
      >,
      ...(instructionsFile ? { config: { model_instructions_file: instructionsFile } } : {}),
    });

    const codexOptions = this.config.driverOptions?.codex;
    const threadOptions = {
      model: this.config.model,
      sandboxMode: codexOptions?.sandboxMode ?? "workspace-write",
      workingDirectory: this.config.cwd,
      skipGitRepoCheck: true,
      modelReasoningEffort: codexOptions?.reasoningEffort ?? "medium",
      networkAccessEnabled: codexOptions?.networkAccessEnabled,
      approvalPolicy: codexOptions?.approvalPolicy ?? "never",
      additionalDirectories: codexOptions?.additionalDirectories,
    } satisfies Parameters<CodexClient["startThread"]>[0];

    this.thread = this.config.resumeSessionId
      ? client.resumeThread(this.config.resumeSessionId, threadOptions)
      : client.startThread(threadOptions);
    this._runtimeSessionId = this.thread.id ?? this.config.resumeSessionId;
    this.log.info("codex driver started", {
      model: this.config.model,
      resumed: Boolean(this.config.resumeSessionId),
      sessionId: this._runtimeSessionId,
    });
  }

  public async prompt(message: string): Promise<void> {
    if (this.managed) return this.managed.prompt(message);
    await this.start();
    if (!this.thread) throw new Error("Codex thread not initialized");
    if (this.running) throw new Error("Codex turn already in progress");

    this.running = true;
    this.pendingToolResults = [];
    this.activeAssistantItemId = null;
    this.activeAssistantText = "";
    this._lastTokens = null;
    this.abortController = new AbortController();
    let turnFinished = false;
    this.log.debug("prompt issued", { length: message.length });

    try {
      const input = message;
      const { events } = await this.thread.runStreamed(input, {
        signal: this.abortController.signal,
      });

      for await (const event of events) {
        turnFinished = this.handleThreadEvent(event) || turnFinished;
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      if (error.name !== "AbortError") {
        this.log.error("prompt failed", error);
        const detail = this.classifyErrorMessage(error.message);
        this.emit("agent-event", {
          type: "error",
          error: detail.message,
          detail,
        } satisfies AgentEvent);
        this.emit("agent-event", { type: "agent_end" } satisfies AgentEvent);
      }
    } finally {
      if (!turnFinished && this.activeAssistantItemId) {
        this.emit("agent-event", {
          type: "message_end",
          message: this.buildAssistantMessage(this.activeAssistantText),
        } satisfies AgentEvent);
      }
      this.activeAssistantItemId = null;
      this.activeAssistantText = "";
      this.abortController = null;
      this.running = false;
    }
  }

  public async abort(): Promise<void> {
    if (this.managed) return this.managed.abort();
    this.abortController?.abort();
  }

  public get isRunning(): boolean {
    if (this.managed) return this.managed.isRunning;
    return this.running;
  }

  public kill(): void {
    if (this.managed) {
      this.managed.kill();
      return;
    }
    this.abortController?.abort();
    this.running = false;
    this.abortController = null;
    this.log.info("codex driver killed");
  }

  override async listModels(): Promise<ModelEntry[]> {
    if (this.managed) return this.managed.listModels();
    const apiKey =
      this.config.apiKeys?.openai || this.config.env?.OPENAI_API_KEY || process.env.OPENAI_API_KEY;
    if (!apiKey) return [];
    const result = await fetchProviderModels("openai", apiKey);
    return result.ok ? (result.models as ModelEntry[]) : [];
  }

  async resetSession(): Promise<void> {
    if (this.managed) return this.managed.resetSession();
    this.kill();
    this.thread = null;
    this._runtimeSessionId = undefined;
    this._lastTokens = null;
  }

  override getTokenUsage(): TokenUsage | null {
    if (this.managed) return this.managed.getTokenUsage();
    return this._lastTokens;
  }

  override reconfigure(patch: Partial<Pick<AgentConfig, "model" | "thinking" | "effort">>): void {
    this.managed?.reconfigure(patch);
  }

  override getModel(): string | undefined {
    return this.managed?.getModel();
  }

  /**
   * Write a combined instructions file for Codex CLI's `model_instructions_file` config.
   * Combines pre-rendered agent identity (from `skaile install`) with runtime context
   * (resources, environment, prompt-extensions).
   */
  private writeInstructionsFile(): string | undefined {
    const parts: string[] = [];

    // 1. Read pre-rendered agent identity if available
    if (this.config.agentDir) {
      const agentFile = join(this.config.cwd, ".codex", "skaile-agent.md");
      if (existsSync(agentFile)) {
        parts.push(readFileSync(agentFile, "utf-8"));
      }
    }

    // 2. Append runtime system prompt (resources, context, environment)
    if (this.config.systemPrompt) {
      parts.push(this.config.systemPrompt);
    }

    if (parts.length === 0) return undefined;

    // Write combined instructions to a session file
    const instrFile = join(this.config.cwd, ".codex", "skaile-session-instructions.md");
    mkdirSync(dirname(instrFile), { recursive: true });
    writeFileSync(instrFile, parts.join("\n\n---\n\n"));
    return instrFile;
  }

  private handleThreadEvent(event: ThreadEvent): boolean {
    switch (event.type) {
      case "thread.started":
        this._runtimeSessionId = event.thread_id;
        return false;

      case "turn.started":
        return false;

      case "item.started":
      case "item.updated":
      case "item.completed":
        this.handleThreadItem(event.item, event.type === "item.completed", event.type);
        return false;

      case "turn.completed": {
        this.flushToolResults();
        // Map Codex SDK `Usage` shape into the unified `TokenUsage`. Note
        // OpenAI's `input_tokens` is the TOTAL including cached reads, not
        // the fresh-prompt count Anthropic reports — consumers that want
        // a strict "fresh input" figure must subtract `cacheReadTokens`.
        const usage = event.usage;
        const tokens: TokenUsage | null = usage
          ? {
              inputTokens: usage.input_tokens,
              outputTokens: usage.output_tokens,
              cacheReadTokens: usage.cached_input_tokens,
            }
          : null;
        this._lastTokens = tokens;
        this.emit("agent-event", {
          type: "result",
          subtype: "success",
          summary: this.activeAssistantText || "Task completed",
          // Codex SDK does not report per-turn cost. `0` signals "not
          // reported" — pair with `tokens` to disambiguate from "free".
          costUsd: 0,
          ...(tokens ? { tokens } : {}),
        } satisfies AgentEvent);
        this.emit("agent-event", { type: "agent_end" } satisfies AgentEvent);
        return true;
      }

      case "turn.failed": {
        this.flushToolResults();
        const detail = this.classifyErrorMessage(event.error.message);
        this.emit("agent-event", {
          type: "error",
          error: detail.message,
          detail,
        } satisfies AgentEvent);
        this.emit("agent-event", { type: "agent_end" } satisfies AgentEvent);
        return true;
      }

      case "error": {
        const detail = this.classifyErrorMessage(event.message);
        this.emit("agent-event", {
          type: "error",
          error: detail.message,
          detail,
        } satisfies AgentEvent);
        this.emit("agent-event", { type: "agent_end" } satisfies AgentEvent);
        return true;
      }
    }
  }

  private handleThreadItem(item: ThreadItem, completed: boolean, phase: ThreadEvent["type"]): void {
    switch (item.type) {
      case "agent_message":
        this.handleAgentMessage(item, completed);
        break;

      case "command_execution":
        this.handleCommandExecution(item, completed, phase);
        break;

      case "mcp_tool_call":
        this.handleMcpToolCall(item, completed, phase);
        break;

      case "file_change":
        this.handleFileChange(item, completed, phase);
        break;

      case "web_search":
        this.handleWebSearch(item, completed, phase);
        break;

      case "error":
        if (completed) {
          this.pendingToolResults.push({
            role: "tool",
            toolName: "CodexError",
            content: "",
            data: { message: item.message },
            isError: true,
          });
        }
        break;

      default:
        break;
    }
  }

  private handleAgentMessage(item: AgentMessageItem, completed: boolean): void {
    if (this.activeAssistantItemId !== item.id) {
      if (this.activeAssistantItemId) {
        this.emit("agent-event", {
          type: "message_end",
          message: this.buildAssistantMessage(this.activeAssistantText),
        } satisfies AgentEvent);
      }
      this.activeAssistantItemId = item.id;
      this.activeAssistantText = "";
      this.emit("agent-event", {
        type: "message_start",
        message: this.buildAssistantMessage(""),
      } satisfies AgentEvent);
    }

    const nextText = item.text ?? "";
    const delta = nextText.startsWith(this.activeAssistantText)
      ? nextText.slice(this.activeAssistantText.length)
      : nextText;
    this.activeAssistantText = nextText;

    if (delta) {
      this.emit("agent-event", {
        type: "message_update",
        message: this.buildAssistantMessage(nextText),
        _textDelta: delta,
      } satisfies AgentEvent);
    }

    if (completed) {
      this.emit("agent-event", {
        type: "message_end",
        message: this.buildAssistantMessage(nextText),
      } satisfies AgentEvent);
      this.activeAssistantItemId = null;
    }
  }

  private handleCommandExecution(
    item: CommandExecutionItem,
    completed: boolean,
    phase: ThreadEvent["type"],
  ): void {
    if (phase === "item.started") {
      this.emit("agent-event", {
        type: "tool_call",
        name: "Bash",
        tool: { name: "Bash" },
        input: { command: item.command },
      } satisfies AgentEvent);
    }

    if (completed) {
      this.pendingToolResults.push({
        role: "tool",
        toolName: "Bash",
        content: "",
        data: {
          stdout: item.aggregated_output,
          stderr: "",
          exitCode: item.exit_code,
          status: item.status,
          command: item.command,
        },
        isError: item.status === "failed",
      });
      this.emit("agent-event", {
        type: "tool_execution_end",
        toolName: "Bash",
      } satisfies AgentEvent);
    }
  }

  private handleMcpToolCall(
    item: McpToolCallItem,
    completed: boolean,
    phase: ThreadEvent["type"],
  ): void {
    const toolName = `mcp__${item.server}__${item.tool}`;
    if (phase === "item.started") {
      this.emit("agent-event", {
        type: "tool_call",
        name: toolName,
        tool: { name: toolName },
        input: this.toObject(item.arguments),
      } satisfies AgentEvent);
    }

    if (completed) {
      this.pendingToolResults.push({
        role: "tool",
        toolName,
        content: "",
        data: {
          status: item.status,
          content: item.result?.content,
          structuredContent: item.result?.structured_content,
          error: item.error?.message,
        },
        isError: item.status === "failed",
      });
      this.emit("agent-event", {
        type: "tool_execution_end",
        toolName,
      } satisfies AgentEvent);
    }
  }

  private handleFileChange(
    item: FileChangeItem,
    completed: boolean,
    phase: ThreadEvent["type"],
  ): void {
    if (phase === "item.started") {
      this.emit("agent-event", {
        type: "tool_call",
        name: "ApplyPatch",
        tool: { name: "ApplyPatch" },
        input: { changes: item.changes },
      } satisfies AgentEvent);
    }

    if (completed) {
      this.pendingToolResults.push({
        role: "tool",
        toolName: "ApplyPatch",
        content: "",
        data: { status: item.status, changes: item.changes },
        isError: item.status === "failed",
      });
      this.emit("agent-event", {
        type: "tool_execution_end",
        toolName: "ApplyPatch",
      } satisfies AgentEvent);
    }
  }

  private handleWebSearch(
    item: WebSearchItem,
    completed: boolean,
    phase: ThreadEvent["type"],
  ): void {
    if (phase === "item.started") {
      this.emit("agent-event", {
        type: "tool_call",
        name: "WebSearch",
        tool: { name: "WebSearch" },
        input: { query: item.query },
      } satisfies AgentEvent);
    }

    if (completed) {
      this.pendingToolResults.push({
        role: "tool",
        toolName: "WebSearch",
        content: "",
        data: { query: item.query, status: "completed" },
      });
      this.emit("agent-event", {
        type: "tool_execution_end",
        toolName: "WebSearch",
      } satisfies AgentEvent);
    }
  }

  private flushToolResults(): void {
    if (this.pendingToolResults.length === 0) return;
    this.emit("agent-event", {
      type: "turn_end",
      toolResults: [...this.pendingToolResults],
    } satisfies AgentEvent);
    this.pendingToolResults = [];
  }

  private buildAssistantMessage(text: string): AgentMessage {
    return {
      role: "assistant",
      content: [{ type: "text", text }],
    };
  }

  private toObject(value: unknown): Record<string, unknown> {
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      return value as Record<string, unknown>;
    }
    return { arguments: value };
  }

  private classifyErrorMessage(message: string): AgentError {
    // Shared keyword buckets live in error-classifier.ts; codex layers its own
    // hint copy plus a provider-specific `config` (sandbox/approval) bucket.
    const generic = classifyGenericErrorMessage(message);
    if (generic) {
      const hint =
        generic.category === "auth"
          ? "Check your OpenAI API key configuration in settings or environment."
          : generic.category === "rate_limit"
            ? "Codex hit an OpenAI rate limit. Wait briefly and try again."
            : generic.category === "model"
              ? "The selected Codex model is unavailable. Choose a different model."
              : "A network error occurred while talking to Codex.";
      return { message, category: generic.category, retryable: generic.retryable, hint };
    }
    const lower = message.toLowerCase();
    if (
      lower.includes("sandbox") ||
      lower.includes("approval") ||
      lower.includes("permission") ||
      lower.includes("git repo")
    ) {
      return {
        message,
        category: "config",
        retryable: false,
        hint: "Check Codex sandbox, approval, and workspace configuration.",
      };
    }
    return {
      message,
      category: "unknown",
      retryable: true,
      hint: "An unexpected Codex error occurred. Try the prompt again.",
    };
  }
}

/**
 * `DriverTarget` wrapper for the codex driver. Registered into the plugin
 * registry by `registerBuiltinDrivers()`. The Codex SDK is lazy-imported inside
 * `start()` — this target export never pulls it at module top.
 */
export const codexDriverTarget: DriverTarget<AgentConfig> = {
  id: "codex",
  displayName: BUILTIN_DRIVER_CATALOG.codex?.name ?? "OpenAI Codex",
  apiVersion: 1,
  // Drivers take AgentConfig; passthrough — full schema validation is out of scope.
  configSchema: z.custom<AgentConfig>(() => true),
  create: async (config, _ctx) => new CodexDriver(config),
  listModels: async (apiKeys) =>
    new CodexDriver({ cwd: process.cwd(), sessionId: "model-list", apiKeys }).listModels(),
};
